<script setup lang="ts">
/**
 * Grande carte de la page publique d'une association, charte « apple »
 * (Karim 2026-10-18), sur le modèle des cartes de la rangée de /cagnotte
 * (CarrouselCartesApple) : surtitre, titre fort, une ou deux lignes, détails
 * (slot), puis l'image qui remplit le bas de la carte et le rond noir « › ».
 * Une seule carte pour les cagnottes, événements, adhésions, boutiques et
 * actualités : le même geste garde la même apparence.
 *
 * Couleur : `--fond-carte` / `--fond-image`, posés par le chapitre (cartes
 * blanches sur fond gris, grises sur fond blanc).
 * `contenir` : l'image entière est montrée, posée sur sa version floutée
 * (visuels de cagnotte souvent au format affiche).
 * `large` : seule de son chapitre → carte horizontale pleine largeur.
 * Sans `to` : un bouton (`@click`), pour les actualités qui s'ouvrent en
 * fenêtre.
 */
import { NuxtLink } from "#components";
import { TITRE_CARTE_APPLE } from "~/utils/titresApple";

defineProps<{
  to?: string;
  image: string | null;
  alt: string;
  /** Icône Lucide affichée quand il n'y a pas d'image. */
  icone: string;
  surtitre?: string | null;
  titre: string;
  description?: string | null;
  contenir?: boolean;
  large?: boolean;
}>();
</script>

<template>
  <component
    :is="to ? NuxtLink : 'button'"
    :to="to"
    :target="to ? '_blank' : undefined"
    :type="to ? undefined : 'button'"
    class="tuile group relative flex shrink-0 snap-start flex-col overflow-hidden rounded-[28px] text-left no-underline"
    :class="large ? 'tuile--large sm:flex-row-reverse' : 'tuile--rangee'"
  >
    <div class="tuile-texte" :class="large && 'sm:flex sm:w-[45%] sm:flex-col sm:justify-center'">
      <p v-if="surtitre" class="tuile-surtitre mb-2 font-semibold text-[#1d1d1f]">{{ surtitre }}</p>
      <h3 class="tuile-titre mb-2.5 line-clamp-2 text-[#1d1d1f] [text-wrap:balance]" :style="TITRE_CARTE_APPLE">{{ titre }}</h3>
      <p v-if="description" class="tuile-ligne line-clamp-2 leading-[1.45] text-[#424245]" :class="large && 'sm:line-clamp-4'">{{ description }}</p>
      <div v-if="$slots.default" class="mt-4">
        <slot />
      </div>
    </div>

    <div class="tuile-media relative mt-5 min-h-[150px] flex-1 overflow-hidden" :class="large && 'sm:mt-0 sm:min-h-[340px] sm:w-[55%] sm:flex-none'">
      <template v-if="image">
        <img v-if="contenir" :src="image" class="absolute inset-0 h-full w-full scale-110 object-cover opacity-60 blur-xl" alt="" loading="lazy" />
        <img
          :src="image"
          :alt="alt"
          class="tuile-image absolute inset-0 h-full w-full"
          :class="contenir ? 'object-contain' : 'object-cover'"
          loading="lazy"
        />
      </template>
      <div v-else class="flex h-full w-full items-center justify-center">
        <UIcon :name="icone" class="size-12 text-[#86868b]" aria-hidden="true" />
      </div>
      <slot name="badge" />
    </div>

    <span class="tuile-rond absolute right-5 bottom-5 flex size-9 items-center justify-center rounded-full bg-[#1d1d1f] text-white lg:size-11" aria-hidden="true">
      <UIcon name="i-lucide-chevron-right" class="size-5" />
    </span>
  </component>
</template>

<style scoped>
.tuile {
  background: var(--fond-carte, #f5f5f7);
  transition:
    transform 0.45s cubic-bezier(0.28, 0, 0.22, 1),
    box-shadow 0.45s ease;
}
.tuile-media {
  background: var(--fond-image, #e8e8ed);
}
.tuile-image,
.tuile-rond {
  transition: transform 0.6s cubic-bezier(0.28, 0, 0.22, 1);
}

/* ── Mesures des cartes de /cagnotte : téléphone / ≥ 735 px / ≥ 1069 px ── */
.tuile--rangee {
  width: 260px;
  height: 440px;
}
.tuile-texte {
  padding: 24px 24px 0;
}
.tuile-surtitre,
.tuile-ligne {
  font-size: 14px;
}
.tuile-titre {
  font-size: 21px;
}
.tuile--large {
  width: 100%;
  min-height: 440px;
}
@media (min-width: 640px) {
  .tuile--large {
    min-height: 0;
  }
  /* Bas plus haut : place pour le rond « › » sous les détails. */
  .tuile--large .tuile-texte {
    padding: 48px 48px 96px;
  }
}
@media (min-width: 735px) {
  .tuile--rangee {
    width: 344px;
    height: 520px;
  }
  .tuile-texte {
    padding: 30px 30px 0;
  }
  .tuile-titre {
    font-size: 24px;
  }
}
@media (min-width: 1069px) {
  .tuile--rangee {
    width: 372px;
    height: 576px;
  }
  .tuile-texte {
    padding: 32px 32px 0;
  }
  .tuile-surtitre,
  .tuile-ligne {
    font-size: 17px;
  }
  .tuile-titre {
    font-size: 28px;
  }
  .tuile--large .tuile-titre {
    font-size: 32px;
  }
}

@media (hover: hover) {
  .tuile:hover {
    transform: translateY(-4px);
    box-shadow: 0 30px 60px -34px rgba(0, 0, 0, 0.35);
  }
  .tuile:hover .tuile-image {
    transform: scale(1.04);
  }
  .tuile:hover .tuile-rond {
    transform: scale(1.08);
  }
}
.tuile:focus-visible {
  outline: none;
  box-shadow: 0 0 0 4px color-mix(in oklab, var(--color-main), transparent 65%);
}
@media (prefers-reduced-motion: reduce) {
  .tuile,
  .tuile-image,
  .tuile-rond {
    transition: none;
  }
}
</style>
